'use client';

import { QueryKeys } from '@/constants/query-keys';
import { AnalyticsEvent } from '@/enums/analytics-type';
import { useDynamicStaleTime } from '@/hooks/shared/dynamic-stale-time';
import { profileApiService } from '@/services/(frontend)/profile/profile-api.service';
import { Profile, ProfileData } from '@cinemark/profile-api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIsAuthReady } from './is-auth-ready';

type UpdateProfileType = {
  analyticsEvent?: AnalyticsEvent;
  profileData: ProfileData;
};

type UpdateProfileWithAnalyticsType = {
  analyticsEvent?: AnalyticsEvent;
  queryKey: readonly string[];
};

export const useProfile = (): ReturnType<typeof useQuery<Profile>> => {
  const updateQueryStaleTime = useDynamicStaleTime();
  const { isAuthenticated } = useIsAuthReady();

  return useQuery<Profile>({
    enabled: isAuthenticated,
    queryFn: async () => {
      const response = await profileApiService.getMyProfile();

      if (response.headers) {
        updateQueryStaleTime(QueryKeys.user.profile, response.headers);
      }

      if (!response.data) {
        throw new Error('Profile not found');
      }

      return response.data;
    },
    queryKey: QueryKeys.user.profile,
  });
};

export const useUpdateProfile = (): ReturnType<typeof useMutation<Profile, unknown, UpdateProfileType, UpdateProfileWithAnalyticsType>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ profileData }: UpdateProfileType) => await profileApiService.updateMyProfile(profileData),
    onMutate: ({ analyticsEvent }) => {
      return { analyticsEvent, queryKey: QueryKeys.user.profile };
    },
    onSuccess: data => queryClient.setQueryData(QueryKeys.user.profile, data),
  });
};
